import type { AttendanceMatrixDto } from './api/attendance.api';

// ─── Qué clases deben asistencia ─────────────────────────────────────────────
//
// LA REGLA VIVE ACÁ Y NO EN LA PANTALLA porque la usan dos: la pestaña de
// Asistencia, que lista lo pendiente, y el Resumen del curso, que lo cuenta
// junto a los otros indicadores. Escrita dos veces se desalinearía, y entonces
// el indicador diría «4» y la pestaña mostraría tres.
//
// UNA JORNADA ESTÁ PENDIENTE CUANDO YA OCURRIÓ Y NADIE MARCÓ A NADIE. Las dos
// mitades importan:
//
//  · YA OCURRIÓ. La matriz trae los días del CRONOGRAMA, no de lo registrado,
//    así que incluye las clases que todavía no se dictaron. Contarlas sería
//    reclamarle al docente un trabajo que nadie podía haber hecho — la misma
//    acusación del calendario que ya se corrigió en el informe de rendimiento.
//
//  · NADIE MARCÓ A NADIE. Con que haya UN alumno con estado, la jornada se
//    tomó: quien marcó y dejó a los ausentes sin tocar hizo su trabajo. Exigir
//    la lista completa convertiría en deuda lo que ya está hecho, y es la forma
//    más rápida de que un indicador deje de creerse.

export interface JornadaPendiente {
  moduleId: string;
  /** ISO corta, `YYYY-MM-DD`. */
  date: string;
}

/**
 * Las jornadas vencidas sin una sola marca, de la más antigua a la más reciente
 * — que es el orden en que conviene saldarlas.
 *
 * `hoy` se recibe en vez de leerse del reloj para que el cálculo sea el mismo
 * en la pantalla y en el test, y para que el día se resuelva en la zona horaria
 * del seminario y no en la del navegador de quien mira.
 */
export function jornadasPendientes(
  matriz: Pick<AttendanceMatrixDto, 'days' | 'students'> | null | undefined,
  hoy: string,
): JornadaPendiente[] {
  if (!matriz) return [];
  const { days, students } = matriz;
  // SIN ALUMNOS NO HAY ASISTENCIA QUE TOMAR. Sin esto, una sección recién
  // creada le mostraba al docente una deuda que no puede saldar: no hay nadie
  // a quien marcar. Lo encontró el test antes que nadie.
  if (students.length === 0) return [];

  return days
    .map((d, i) => ({ dia: d, i }))
    .filter(({ dia, i }) => {
      if (dia.date > hoy) return false;
      // `some` y no `every`: basta una marca para que la jornada esté tomada.
      return !students.some((s) => (s.statuses[i] ?? 'none') !== 'none');
    })
    .map(({ dia }) => ({ moduleId: dia.moduleId, date: dia.date }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * La jornada por la que conviene abrir la pestaña: la de HOY si hay clase hoy
 * —es la que el docente viene a marcar— y si no, la pendiente más antigua.
 *
 * `null` cuando no hay nada que hacer, y entonces la pantalla no abre ningún
 * diálogo: una pantalla que se abre sola sobre algo que no pediste es peor que
 * una que espera.
 */
export function jornadaParaAbrir(
  pendientes: JornadaPendiente[],
  hoy: string,
): JornadaPendiente | null {
  return pendientes.find((p) => p.date === hoy) ?? pendientes[0] ?? null;
}
